import { useState } from 'react';
import { Alert, Box, Button, Card, FormControl, FormLabel, Input, Option, Select, Stack, Typography } from '@mui/joy';
import { Plus, Users } from '@phosphor-icons/react';
import { api } from '../../../lib/api';
import { getApiErrorMessage } from '../../../lib/apiError';
import type { Contract } from '../../../types';

type Recipient = { email: string; name: string; role: string; signing_order: number };
const roles = ['customer', 'sales_rep', 'manager', 'witness', 'signer', 'cc', 'approver'];

export function ContractRecipientsEditor({ contract, onSaved, onDirtyChange }: { contract: Contract; onSaved: () => Promise<void>; onDirtyChange: (dirty: boolean) => void }) {
  const [editing, setEditing] = useState(false);
  const [recipients, setRecipients] = useState<Recipient[]>(contract.recipients || []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const missingCustomer = !recipients.some(r => r.role === 'customer');
  const update = (index: number, changes: Partial<Recipient>) => {
    setEditing(true); onDirtyChange(true);
    setRecipients(rows => rows.map((row, i) => i === index ? { ...row, ...changes } : row));
  };
  const add = (customer = false) => {
    setEditing(true); onDirtyChange(true);
    setRecipients(rows => [...rows, {
      name: customer ? contract.customer_name || '' : '',
      email: customer ? contract.customer_email || '' : '',
      role: customer ? 'customer' : 'signer', signing_order: rows.length + 1,
    }]);
  };
  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true); setError('');
    const normalized = recipients.map((r, index) => ({ ...r, name: r.name.trim(), email: r.email.trim(), signing_order: index + 1 }));
    if (normalized.some(r => !r.name || !r.email)) {
      setError('Enter a name and email address for each recipient.'); setBusy(false); return;
    }
    if (new Set(normalized.map(r => r.email.toLowerCase())).size !== normalized.length) {
      setError('Each recipient needs a unique email address.'); setBusy(false); return;
    }
    try {
      await api.patch(`/api/crm/contracts/${contract.id}/update_signing_order/`, { recipients: normalized });
      await onSaved(); setEditing(false); onDirtyChange(false);
    } catch (e) { setError(getApiErrorMessage(e, 'Could not save recipients. Please try again.')); }
    finally { setBusy(false); }
  };
  return <Card component="form" onSubmit={save}>
    <Typography level="title-lg" startDecorator={<Users />}>Who needs to sign?</Typography>
    <Typography level="body-sm">The linked customer is not automatically a recipient on every contract. Confirm names, email addresses, and roles before sending.</Typography>
    {error && <Alert color="danger">{error}</Alert>}
    {missingCustomer && <Alert color="warning"><Stack gap={1}>
      <Typography level="body-sm">The customer is not on the recipient list.</Typography>
      <Button type="button" variant="outlined" disabled={busy} onClick={() => add(true)}>Add linked customer</Button>
    </Stack></Alert>}
    {recipients.map((recipient, index) => <Box key={index} sx={{ borderBottom: '1px solid', borderColor: 'divider', py: 1 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
        <FormControl sx={{ flex: 1, minWidth: 0 }} required><FormLabel>Name</FormLabel><Input aria-label={`Recipient ${index + 1} name`} required value={recipient.name} disabled={busy} onChange={e => update(index, { name: e.target.value })} /></FormControl>
        <FormControl sx={{ flex: 1, minWidth: 0 }} required><FormLabel>Email</FormLabel><Input aria-label={`Recipient ${index + 1} email`} type="email" required value={recipient.email} disabled={busy} onChange={e => update(index, { email: e.target.value })} /></FormControl>
      </Stack>
      <Stack direction="row" gap={1} alignItems="end" justifyContent="space-between" sx={{ mt: 1 }}>
        <FormControl sx={{ minWidth: 160 }}><FormLabel>Fields assigned to</FormLabel><Select aria-label={`Recipient ${index + 1} role`} value={recipient.role} disabled={busy} onChange={(_, role) => role && update(index, { role })}>
          {[...new Set([...roles, ...contract.contract_fields.map(f => f.signer), recipient.role])].filter(Boolean).map(role => <Option key={role} value={role}>{role.replaceAll('_', ' ')}</Option>)}
        </Select></FormControl>
        <Button type="button" color="danger" variant="plain" disabled={busy} onClick={() => { setEditing(true); onDirtyChange(true); setRecipients(rows => rows.filter((_, i) => i !== index)); }}>Remove recipient {index + 1}</Button>
      </Stack>
    </Box>)}
    <Typography level="body-xs">Roles must match the document fields (for example, customer or sales rep). CC recipients receive a copy and do not sign.</Typography>
    <Stack direction="row" gap={1} flexWrap="wrap">
      <Button type="button" variant="outlined" startDecorator={<Plus />} disabled={busy} onClick={() => add()}>Add recipient</Button>
      {editing && <><Button type="submit" loading={busy}>Save recipients</Button><Button type="button" variant="plain" disabled={busy} onClick={() => { setRecipients(contract.recipients || []); setEditing(false); onDirtyChange(false); setError(''); }}>Cancel changes</Button></>}
    </Stack>
    <Typography level="body-sm">{editing ? 'Save your recipient changes before reviewing and sending.' : 'Saving recipients does not send an invitation.'}</Typography>
  </Card>;
}
